import * as Sentry from '@sentry/react';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { useHistory, useLocation } from 'react-router';
import * as Yup from 'yup';

import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import { Step } from 'src/app/components/Pages/GetStartedNew/StepLayout/Step';
import { useCreateLead } from 'src/app/hooks/data/lead';
import { useGetStartedPersistentData } from 'src/app/hooks/feature_data/useGetStartedPersistentData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import {
  getStartedNew,
  getStartedNewWaitlistConfirmation,
  hipaa,
  privacy,
  termsAndConditions,
} from 'src/app/router/paths';

import { ButtonContainer, StyledButton, LegalMessage } from '../../styles';

export const Waitlist = () => {
  const history = useHistory();
  const isGettingStartedForm = true;

  const { getStartedPersistentData } = useGetStartedPersistentData();

  const { mutateAsync: createLead } = useCreateLead();

  const trackJoinDeliveryAreaWailist = useSegmentEvent('joinDeliveryAreaWaitlist', {
    shouldTrackOnMount: false,
  });

  const trackCreateLeadSegmentEvent = useSegmentEvent('createLead', {
    shouldTrackOnMount: false,
  });

  const trackCreateWaitlistLeadSegmentEvent = useSegmentEvent('createWaitlistLead', {
    shouldTrackOnMount: false,
  });

  const location = useLocation();

  const handleSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
    formikHelpers.setSubmitting(true);

    try {
      const body = {
        zipCode: getStartedPersistentData && getStartedPersistentData.zipCode ? getStartedPersistentData.zipCode : '',
        email: values.email,
        source: 'onboarding_stay_in_touch',
      };
      await createLead(body);

      formikHelpers.setSubmitting(false);

      history.push(getStartedNewWaitlistConfirmation.route);

      trackJoinDeliveryAreaWailist({ pagepath: location.pathname });
      trackCreateLeadSegmentEvent();
      trackCreateWaitlistLeadSegmentEvent();
    } catch (error) {
      Sentry.captureException(error);
      formikHelpers.setErrors(extractValidationErrors(error));
      formikHelpers.setSubmitting(false);
    }
  };

  return (
    <Step title="Get early access" text={''} handlePrev={() => history.push(getStartedNew.route)}>
      <Formik
        initialValues={{ email: '' }}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          email: Yup.string().email('Please enter a valid email address.').required(),
        })}
      >
        {({ isValid }) => {
          return (
            <Form>
              <FormikTextField name="email" type="email" label={{ content: 'Email', isHidden: false }} />
              <ButtonContainer>
                <StyledButton size="medium" disabled={!isValid} type="submit" style={'capsuleBlue50'}>
                  Notify Me
                </StyledButton>
                {isGettingStartedForm && (
                  <StyledButton
                    style="tertiary"
                    className="pl-0"
                    size="small"
                    onClick={() => history.push(getStartedNew.route)}
                    type="button"
                  >
                    Try Another Zip Code
                  </StyledButton>
                )}
              </ButtonContainer>
              <LegalMessage>
                By clicking notify me, I accept the{' '}
                <LinkWithQuery to={termsAndConditions.toPath()} target="_blank">
                  Capsule Terms of Service
                </LinkWithQuery>
                {', '}
                <LinkWithQuery to={privacy.toPath()} target="_blank">
                  Privacy Policy
                </LinkWithQuery>
                {', and '}
                <LinkWithQuery to={hipaa.toPath()} target="_blank">
                  HIPAA Policy
                </LinkWithQuery>
              </LegalMessage>
            </Form>
          );
        }}
      </Formik>
    </Step>
  );
};
